import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Empty, Form, Input, InputNumber, message, Modal, Select,
  Space, Table, Tag, Typography
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined
} from '@ant-design/icons';
import { fixedAssetsApi } from './fixedAssetsApi';
import {
  DEPRECIATION_METHODS, friendlyErrorFor
} from './fixedAssetsHelpers';
import { friendlyAccountsEmptyMessage } from '../accountsPageState';
import '../accounts.css';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Asset Categories panel (Stage 6B.9D).
//
// Simple master list — code, name, default depreciation method/rate,
// useful life, active flag. Lives inside the FixedAssetsPage as a
// secondary tab. Default-ledger picking is parked for a later stage
// to keep the foundation small; for now operators can leave ledgers
// blank and set them per-asset.
// ─────────────────────────────────────────────────────────────────

function CategoryFormModal({ open, mode, record, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (mode === 'edit' && record) {
      form.setFieldsValue({
        categoryCode:              record.categoryCode,
        categoryName:              record.categoryName,
        defaultDepreciationMethod: record.defaultDepreciationMethod,
        defaultDepreciationRate:   record.defaultDepreciationRate,
        defaultUsefulLifeYears:    record.defaultUsefulLifeYears,
        isActive:                  record.isActive !== false,
        remarks:                   record.remarks
      });
    } else {
      form.setFieldsValue({
        defaultDepreciationMethod: 'wdv',
        isActive: true
      });
    }
  }, [open, mode, record, form]);

  const save = useCallback(async () => {
    try {
      const v = await form.validateFields();
      setBusy(true);
      let saved;
      if (mode === 'add') {
        saved = await fixedAssetsApi.createCategory(v);
        message.success(`Category ${saved.categoryCode} created.`);
      } else {
        saved = await fixedAssetsApi.updateCategory(record.id, v);
        message.success('Category updated.');
      }
      onSaved && onSaved(saved);
    } catch (e) {
      if (e && e.errorFields) return;
      message.error(friendlyErrorFor(e));
    } finally { setBusy(false); }
  }, [form, mode, record, onSaved]);

  return (
    <Modal
      title={mode === 'add' ? 'New Fixed Asset Category' : `Edit Category — ${record?.categoryCode}`}
      open={open}
      onCancel={onClose}
      onOk={save}
      okText={mode === 'add' ? 'Create' : 'Save'}
      confirmLoading={busy}
      centered
      destroyOnClose
      width={620}
      maskClosable={false}
      className="erp-accounts-modal"
    >
      <Form form={form} layout="vertical" preserve className="erp-accounts-form">
        <Form.Item label="Category Code" name="categoryCode"
          rules={[
            { required: true, message: 'Category code is required.' },
            { pattern: /^[A-Z0-9_-]+$/, message: 'Uppercase letters, digits, hyphens and underscores only.' }
          ]}>
          <Input maxLength={40} placeholder="e.g. PLANT_MACHINERY" />
        </Form.Item>
        <Form.Item label="Category Name" name="categoryName"
          rules={[{ required: true, message: 'Category name is required.' }]}>
          <Input maxLength={120} placeholder="e.g. Plant & Machinery" />
        </Form.Item>
        <Form.Item label="Default Depreciation Method" name="defaultDepreciationMethod">
          <Select allowClear
            options={DEPRECIATION_METHODS.map((m) => ({ value: m.value, label: m.label }))} />
        </Form.Item>
        <Space size="middle" style={{ width: '100%' }}>
          <Form.Item label="Default Rate (%)" name="defaultDepreciationRate">
            <InputNumber min={0} max={100} step={0.5} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item label="Default Useful Life (years)" name="defaultUsefulLifeYears">
            <InputNumber min={0} step={1} style={{ width: 160 }} />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Select style={{ width: 120 }} options={[
              { value: true, label: 'Active' }, { value: false, label: 'Inactive' }
            ]} />
          </Form.Item>
        </Space>
        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default function FixedAssetCategoriesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [modalRecord, setModalRecord] = useState(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const out = await fixedAssetsApi.listCategories({ page: 1, pageSize: 200 });
      setRows((out && out.rows) || []);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openAdd  = useCallback(() => { setModalMode('add'); setModalRecord(null); setModalOpen(true); }, []);
  const openEdit = useCallback((r) => { setModalMode('edit'); setModalRecord(r); setModalOpen(true); }, []);
  const onSaved  = useCallback(async () => { setModalOpen(false); await fetchCategories(); }, [fetchCategories]);

  const handleDelete = useCallback(async (record) => {
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete category ${record.categoryCode}?`,
        content: 'The category will be soft-deleted. Categories linked to active assets cannot be deleted.',
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await fixedAssetsApi.deleteCategory(record.id);
      message.success('Category deleted.');
      await fetchCategories();
    } catch (e) { message.error(friendlyErrorFor(e)); }
  }, [fetchCategories]);

  const columns = useMemo(() => [
    { title: 'Code', dataIndex: 'categoryCode', key: 'categoryCode', width: 160,
      render: (v) => <code className="erp-grid__code">{v}</code> },
    { title: 'Name', dataIndex: 'categoryName', key: 'categoryName' },
    { title: 'Default Method', dataIndex: 'defaultDepreciationMethod', key: 'defaultDepreciationMethod',
      width: 130,
      render: (v) => v ? v.toUpperCase() : <span className="erp-grid__muted">—</span> },
    { title: 'Default Rate', dataIndex: 'defaultDepreciationRate', key: 'defaultDepreciationRate',
      width: 110, align: 'right',
      render: (v) => (v != null ? `${Number(v)}%` : <span className="erp-grid__muted">—</span>) },
    { title: 'Useful Life', dataIndex: 'defaultUsefulLifeYears', key: 'defaultUsefulLifeYears',
      width: 100, align: 'right',
      render: (v) => (v != null ? `${Number(v)} yrs` : <span className="erp-grid__muted">—</span>) },
    { title: 'Active', dataIndex: 'isActive', key: 'isActive', width: 90,
      render: (v) => v ? <Tag color="green">Active</Tag> : <Tag color="default">Inactive</Tag> },
    { title: '', key: 'actions', width: 100, align: 'right',
      render: (_v, row) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(row)} />
        </Space>
      ) }
  ], [openEdit, handleDelete]);

  return (
    <div className="erp-accounts-page">
      <Space style={{ marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
        <Text strong>Fixed Asset Categories</Text>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchCategories} loading={loading}>
            Refresh
          </Button>
          <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            New Category
          </Button>
        </Space>
      </Space>
      {loadError ? (
        <Alert type="error" showIcon style={{ marginBottom: 10 }}
          message="Could not load fixed asset categories"
          description={friendlyErrorFor(loadError)}
          action={<Button size="small" icon={<ReloadOutlined />} onClick={fetchCategories}>Retry</Button>} />
      ) : null}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 25, size: 'small' }}
        locale={{ emptyText: <Empty description="No fixed asset categories yet." /> }}
      />
      <CategoryFormModal
        open={modalOpen}
        mode={modalMode}
        record={modalRecord}
        onClose={() => setModalOpen(false)}
        onSaved={onSaved}
      />
    </div>
  );
}
